import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { formatCount } from "@/data/stats";
import { symbolAriaLabel } from "@/lib/symbol-aria";
import { searchSymbols, loadSearchIndex } from "@/lib/search";

interface Item {
  symbol: string;
  slug: string;
  name: string;
  code: string;
  categoryName: string;
}

interface SymbolSearchProps {
  placeholder?: string;
  label?: string;
  autoFocus?: boolean;
  maxResults?: number;
}

/**
 * Global "/" hotkey — focus the search input from anywhere on the page, the
 * way GitHub / Slack / Linear do it. Skipped while the user is typing in any
 * editable field so it never hijacks a literal "/" character.
 */
function isEditableTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
}

export function SymbolSearch({
  placeholder,
  label = "Search all symbols by description, name, character, or Unicode codepoint",
  autoFocus = false,
  maxResults = 8,
}: SymbolSearchProps) {
  const resolvedPlaceholder =
    placeholder ?? `Describe a symbol — try "arrow with hook" — or type / to focus`;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [liveMsg, setLiveMsg] = useState("");

  const blurTimer = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const liveTimer = useRef<number | null>(null);

  const navigate = useNavigate();
  const rid = useId();
  const inputId = `global-search-${rid}`;
  const listboxId = `global-search-listbox-${rid}`;
  const helpId = `global-search-help-${rid}`;
  const statusId = `global-search-status-${rid}`;

  // Register the global "/" focus shortcut. We attach once on mount so the
  // hotkey works on every page that renders <SymbolSearch />, not just the
  // first one. Bail early when the user is mid-typing in a field, when a
  // modifier is held (so Cmd+/ etc. pass through), or when the input is
  // already focused.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;
      const input = inputRef.current;
      if (!input || document.activeElement === input) return;
      e.preventDefault();
      input.focus();
      input.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lazy-load the ~800 KB search index on first interaction so it never sits in
  // the homepage's critical bundle. `indexReady` flips once it resolves, which
  // re-runs the suggestions memo.
  const [indexReady, setIndexReady] = useState(false);
  const ensureIndex = useCallback(() => {
    if (indexReady) return;
    loadSearchIndex().then(() => setIndexReady(true));
  }, [indexReady]);

  const q = query.trim();
  const suggestions: Item[] = useMemo(() => {
    if (!q || !indexReady) return [];
    return searchSymbols(q, maxResults).map((e) => ({
      symbol: e.symbol,
      slug: e.slug,
      name: e.name,
      code: e.code.toUpperCase(),
      categoryName: e.categoryName,
    }));
  }, [q, maxResults, indexReady]);

  void formatCount;

  const listboxOpen = open && suggestions.length > 0;

  useEffect(() => {
    if (liveTimer.current) window.clearTimeout(liveTimer.current);
    if (!q) {
      setLiveMsg("");
      return;
    }
    liveTimer.current = window.setTimeout(() => {
      if (suggestions.length === 0) {
        setLiveMsg(`No suggestions for ${query}.`);
      } else {
        const top = suggestions[0];
        setLiveMsg(
          `${suggestions.length} ${suggestions.length === 1 ? "suggestion" : "suggestions"} available. Top result: ${symbolAriaLabel(top)} Use Up and Down arrows to navigate, Enter to open.`,
        );
      }
    }, 250);
    return () => {
      if (liveTimer.current) window.clearTimeout(liveTimer.current);
    };
  }, [q, query, suggestions]);

  useEffect(() => {
    if (!listboxOpen || activeIdx < 0) return;
    optionRefs.current[activeIdx]?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, listboxOpen]);

  function closeList() {
    setOpen(false);
    setActiveIdx(-1);
  }

  function goTo(it: Item) {
    closeList();
    setQuery("");
    setLiveMsg(`Opening ${it.name}.`);
    navigate({ to: "/symbol/$slug", params: { slug: it.slug } });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      if (!open) {
        setOpen(true);
        return;
      }
      if (suggestions.length === 0) return;
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      if (suggestions.length === 0) return;
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Home" && listboxOpen) {
      e.preventDefault();
      setActiveIdx(0);
    } else if (e.key === "End" && listboxOpen) {
      e.preventDefault();
      setActiveIdx(suggestions.length - 1);
    } else if (e.key === "Enter") {
      if (!listboxOpen) return;
      const pick = activeIdx >= 0 ? suggestions[activeIdx] : suggestions[0];
      if (pick) {
        e.preventDefault();
        goTo(pick);
      }
    } else if (e.key === "Escape") {
      if (listboxOpen) {
        e.preventDefault();
        closeList();
      } else if (query) {
        e.preventDefault();
        setQuery("");
      }
    } else if (e.key === "Tab") {
      closeList();
    }
  }

  const activeOptionId =
    listboxOpen && activeIdx >= 0 && suggestions[activeIdx]
      ? `g-sugg-${rid}-${suggestions[activeIdx].slug}`
      : undefined;

  return (
    <div className="relative">
      <label htmlFor={inputId} className="sr-only">
        {label}
      </label>
      <div
        role="combobox"
        aria-haspopup="listbox"
        aria-owns={listboxId}
        aria-expanded={listboxOpen}
        className="glass flex items-center gap-2 rounded-2xl px-4 py-3 focus-within:ring-2 focus-within:ring-ring"
      >
        <span aria-hidden="true" className="text-lg text-muted-foreground">⌕</span>
        <input
          ref={inputRef}
          id={inputId}
          type="search"
          inputMode="search"
          autoComplete="off"
          spellCheck={false}
          autoFocus={autoFocus}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
          aria-describedby={`${helpId} ${statusId}`}
          placeholder={resolvedPlaceholder}
          value={query}
          onChange={(e) => {
            ensureIndex();
            setQuery(e.target.value);
            setOpen(true);
            setActiveIdx(-1);
          }}
          onFocus={() => {
            ensureIndex();
            setOpen(true);
          }}
          onBlur={() => {
            blurTimer.current = window.setTimeout(() => setOpen(false), 120);
          }}
          onKeyDown={onKeyDown}
          className="w-full bg-transparent text-base outline-none placeholder:text-muted-foreground"
        />
        {query ? (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              closeList();
              inputRef.current?.focus();
            }}
            className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Clear search"
          >
            Clear
          </button>
        ) : (
          <kbd
            aria-hidden="true"
            title="Press / from anywhere to focus search"
            className="hidden rounded border border-border bg-surface/70 px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground sm:inline"
          >
            /
          </kbd>
        )}
      </div>

      <ul
        id={listboxId}
        role="listbox"
        aria-label="Symbol suggestions"
        hidden={!listboxOpen}
        onMouseDown={(e) => {
          e.preventDefault();
          if (blurTimer.current) window.clearTimeout(blurTimer.current);
        }}
        className="glass absolute left-0 right-0 top-full z-30 mt-2 max-h-96 overflow-auto rounded-2xl p-1 shadow-lg"
      >
        {suggestions.map((it, i) => {
          const selected = i === activeIdx;
          return (
            <li
              key={it.slug}
              ref={(el) => {
                optionRefs.current[i] = el;
              }}
              id={`g-sugg-${rid}-${it.slug}`}
              role="option"
              aria-selected={selected}
              aria-label={symbolAriaLabel(it)}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => goTo(it)}
              className={`flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2 text-sm ${
                selected ? "bg-secondary text-foreground" : ""
              }`}
            >
              <span
                aria-hidden="true"
                className="flex h-10 w-10 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-2xl"
              >
                {it.symbol}
              </span>
              <span aria-hidden="true" className="min-w-0 flex-1">
                <span className="block truncate font-medium">{it.name}</span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  {it.code} · {it.categoryName}
                </span>
              </span>
              <span aria-hidden="true" className="text-xs text-muted-foreground">↵</span>
            </li>
          );
        })}
      </ul>

      <p id={helpId} className="mt-2 px-1 text-xs text-muted-foreground">
        Try <kbd className="rounded border border-border bg-surface/60 px-1">arrow with hook</kbd>,{" "}
        <kbd className="rounded border border-border bg-surface/60 px-1">★</kbd>, or{" "}
        <kbd className="rounded border border-border bg-surface/60 px-1">u+2605</kbd>.
        Press <kbd className="rounded border border-border bg-surface/60 px-1">/</kbd> anywhere to focus.
      </p>

      <div
        id={statusId}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {liveMsg}
      </div>
    </div>
  );
}
